import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import './Header.css';

interface HeaderProps {
  page?: 'dashboard' | 'users' | 'restaurants' | 'orders' | 'deals' | 'transactions' | 'documents' | 'homepage';
}

// Profile Dropdown Component
const ProfileDropdown: React.FC = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleLogout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with logout even if API call fails
    } finally {
      logout();
      navigate('/signin');
    }
  };

  return (
    <div className="header-user" ref={dropdownRef}>
      <div 
        className="header-user-content"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <div className="header-avatar">JD</div>
        <span className="header-dropdown">▼</span>
      </div>
      {showDropdown && (
        <div className="header-user-dropdown">
          <button className="dropdown-item" onClick={handleLogout}>
            <span>🚪</span> Logout
          </button>
        </div>
      )}
    </div>
  );
};

const Header: React.FC<HeaderProps> = ({ page = 'dashboard' }) => {

  const getBreadcrumbs = () => {
    switch (page) {
      case 'users':
        return ['Home', 'Users'];
      case 'restaurants':
        return ['Home', 'Restaurants'];
      case 'orders':
        return ['Home', 'Orders'];
      case 'transactions':
        return ['Home', 'Transactions'];
      case 'documents':
        return ['Home', 'Documents'];
      case 'homepage':
        return ['Home', 'Homepage'];
      default:
        return ['Home', 'Dashboard'];
    }
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <header className="dashboard-header">
      <div className="header-left">
        <button className="header-menu-btn">☰</button>
        <nav className="header-breadcrumbs">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              <span>{crumb}</span>
              {index < breadcrumbs.length - 1 && (
                <span className="breadcrumb-separator">›</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      </div>
      <div className="header-center">
        <div className="header-search">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search orders, reservations, customers..."
            className="search-input"
          />
        </div>
      </div>
      <div className="header-right">
        {page === 'dashboard' && (
          <>
            <button className="header-icon-btn">🔔</button>
            <button className="header-icon-btn">❓</button>
            <ProfileDropdown />
          </>
        )}
        {page === 'users' && (
          <>
            <button className="header-icon-btn">🔔</button>
            <button className="header-icon-btn">❓</button>
            <ProfileDropdown />
          </>
        )}
        {page === 'restaurants' && (
          <>
            <button className="header-icon-btn">🔔</button>
            <button className="header-icon-btn">❓</button>
            <ProfileDropdown />
          </>
        )}
        {page === 'deals' && (
          <>
            <button className="header-icon-btn">🔔</button>
            <button className="header-icon-btn">❓</button>
            <ProfileDropdown />
          </>
        )}
        {page === 'orders' && (
          <>
            <button className="header-icon-btn">🔔</button>
            <button className="header-icon-btn">❓</button>
            <ProfileDropdown />
          </>
        )}
        {page === 'homepage' && (
          <>
            <button className="header-icon-btn">🔔</button>
            <button className="header-icon-btn">❓</button>
            <ProfileDropdown />
          </>
        )}
      </div>
    </header>
  );
};

export default Header;

